import { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import PageHeader from '../../../components/PageHeader/PageHeader';
import SummaryCards from '../../../components/Reports/SummaryCards';
import ProfitableProductsTable from '../../../components/Reports/Cost/ProfitableProductsTable';
import AverageCostByCategoryChart from '../../../components/Reports/Cost/AverageCostByCategoryChart';
import MostImpactfulIngredientsChart from '../../../components/Reports/Cost/MostImpactfulIngredientsChart';
import AverageMarginByMonthChart from '../../../components/Reports/Cost/AverageMarginByMonthChart';
import MarginDistributionChart from '../../../components/Reports/Cost/MarginDistributionChart';

import { getCostsDashboardData } from '../../../services/ReportsCostsService';

import PercentIcon from '@mui/icons-material/Percent';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { formatMoney } from '../../../utils/currency';

import useReportCurrency from '../../../hooks/useReportCurrency';
import ReportCurrencyPicker from '../../../components/Currency/ReportCurrencyPicker';
const CostsReports = () => {
  const [reportCurrency, setReportCurrency] = useReportCurrency();
  const { t } = useTranslation();
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [year, setYear] = useState(dayjs().year());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const result = await getCostsDashboardData({ month, year });
      setData(result);
      setLoading(false);
    };
    fetch();
  }, [month, year, reportCurrency]);

  const summaryItems = data?.summary
    ? [
        {
          title: 'Margen promedio',
          icon: <PercentIcon color="success" fontSize="large" />,
          value: `${data.summary.averageMargin}%`,
        },
        {
          title: 'Costo promedio',
          icon: <MonetizationOnIcon color="primary" fontSize="large" />,
          value: `${formatMoney(data.summary.averageCost)}`,
        },
        {
          title: 'Producto con menor margen',
          icon: <WarningAmberIcon color="warning" fontSize="large" />,
          value: data.summary.leastMarginProduct || '-',
        },
        {
          title: 'Producto más rentable',
          icon: <EmojiEventsIcon color="secondary" fontSize="large" />,
          value: data.summary.mostProfitableProduct || '-',
        },
      ]
    : [];

  return (
    <Box p={3}>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item>
          <PageHeader title={t('Recetas y Costos')} subtitle={t('Análisis de rentabilidad de productos e insumos')} />
      <ReportCurrencyPicker currency={reportCurrency} onChange={setReportCurrency} />
        </Grid>
      </Grid>

      <Box mt={2}>
        <SummaryCards items={summaryItems} loading={loading} />
      </Box>

      <Grid container spacing={2} mt={0}>
        <Grid item xs={12} md={8}>
          <ProfitableProductsTable
            data={data?.leastProfitableProducts || []}
            loading={loading}
            title="Productos menos rentables"
            sortOrder="asc"
            sortBy="margin"
          />
        </Grid>
        <Grid item xs={4}>
          <AverageCostByCategoryChart
            data={data?.averageCostByCategory || []}
            loading={loading}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={0}>
        <Grid item xs={12} md={6}>
          <MostImpactfulIngredientsChart
            data={data?.mostImpactfulIngredients || []}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AverageMarginByMonthChart
            data={data?.averageMarginByMonth || []}
            loading={loading}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={0} mb={6}>
        <Grid item xs={12} md={8}>
          <ProfitableProductsTable
            data={data?.hightMarginProducts || []}
            loading={loading}
            title="Productos más rentables"
            sortBy="margin"
            sortOrder="desc"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MarginDistributionChart
            data={data?.marginDistribution || []}
            loading={loading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CostsReports;
